import React, { useState, useEffect } from 'react';
import { authFetch, API } from '../../api';
import { useAuth } from '../../AuthContext';
import { Clock, TrendingUp, TrendingDown, Minus, MessageSquare, X } from 'lucide-react';

export default function GradeHistory({ playerId, onClose }) {
  const { user } = useAuth();
  const [history, setHistory] = useState([]);
  const [notes, setNotes] = useState([]);
  const [activeTab, setActiveTab] = useState('grades');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [histRes, notesRes] = await Promise.all([
          authFetch(`${API}/api/grade-history/${playerId}`),
          authFetch(`${API}/api/evaluator-notes/${playerId}`)
        ]);
        if (histRes.ok) setHistory(await histRes.json());
        if (notesRes.ok) setNotes(await notesRes.json());
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    load();
  }, [playerId]);

  const getTrendIcon = (oldG, newG) => {
    if (oldG === null || oldG === undefined) return <span className="text-blue-400 text-xs">NEW</span>;
    if (newG > oldG) return <TrendingUp className="w-4 h-4 text-green-400" />;
    if (newG < oldG) return <TrendingDown className="w-4 h-4 text-red-400" />;
    return <Minus className="w-4 h-4 text-yellow-400" />;
  };

  const gradeColor = (g) => {
    if (g >= 8) return '#00E676';
    if (g >= 7) return '#66BB6A';
    if (g >= 6) return '#FFD600';
    if (g >= 4) return '#FFB300';
    return '#EF5350';
  };

  const formatDate = (d) => {
    if (!d) return '—';
    const s = d.split(' ')[0] || d.split('T')[0] || d;
    return s;
  };

  // Group history by criterion
  const grouped = {};
  history.forEach(h => {
    if (!grouped[h.criterion_name]) grouped[h.criterion_name] = [];
    grouped[h.criterion_name].push(h);
  });

  return (
    <div className="sheet-overlay fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-4">
      <div className="sheet bg-[#1A2744] rounded-xl border border-[#546E7A]/30 w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-4 md:p-5 border-b border-[#546E7A]/30">
          <h3 className="text-white font-semibold text-lg flex items-center gap-2">
            <Clock className="w-5 h-5 text-[#00D4FF]" />
            Evaluation History
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        {/* Tabs */}
        <div className="chip-row md:flex md:gap-4 px-4 md:px-5 pt-3 border-b border-[#546E7A]/30">
          <button onClick={() => setActiveTab('grades')}
            className={`pb-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'grades' ? 'border-[#00D4FF] text-[#00D4FF]' : 'border-transparent text-gray-400 hover:text-white'}`}>
            Grade Changes ({history.length})
          </button>
          <button onClick={() => setActiveTab('notes')}
            className={`pb-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'notes' ? 'border-[#00D4FF] text-[#00D4FF]' : 'border-transparent text-gray-400 hover:text-white'}`}>
            Evaluator Notes ({notes.length})
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto scroll-y p-4 md:p-5">
          {loading ? (
            <div className="text-center text-gray-400 py-8">Loading history...</div>
          ) : activeTab === 'grades' ? (
            /* Grade History */
            history.length === 0 ? (
              <div className="text-center text-gray-400 py-8">
                <Clock className="w-8 h-8 text-gray-600 mx-auto mb-2" />
                <p>No grade changes recorded yet.</p>
                <p className="text-gray-500 text-xs mt-1">History will appear here as grades are updated.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {Object.entries(grouped).map(([criterion, changes]) => (
                  <div key={criterion} className="bg-[#0D1229] rounded-lg border border-[#546E7A]/20 overflow-hidden">
                    <div className="px-4 py-2 border-b border-[#546E7A]/20 flex justify-between items-center">
                      <span className="text-white font-medium text-sm">{criterion}</span>
                      <span className="text-gray-500 text-xs">{changes.length} change{changes.length !== 1 ? 's' : ''}</span>
                    </div>
                    <div className="divide-y divide-[#546E7A]/10">
                      {changes.map((h, idx) => (
                        <div key={h.id} className="px-4 py-2.5 flex items-center gap-3">
                          {getTrendIcon(h.old_grade, h.new_grade)}
                          <span className="text-gray-500 text-xs w-20 flex-shrink-0">{formatDate(h.changed_at)}</span>
                          <div className="flex items-center gap-2">
                            {h.old_grade !== null && (
                              <>
                                <span className="text-sm font-bold" style={{ color: gradeColor(h.old_grade) }}>{h.old_grade}</span>
                                <span className="text-gray-500 text-xs">→</span>
                              </>
                            )}
                            <span className="text-sm font-bold" style={{ color: gradeColor(h.new_grade) }}>{h.new_grade}</span>
                          </div>
                          {h.scout_name && <span className="text-gray-500 text-xs">by {h.scout_name}</span>}
                          {h.notes && <span className="text-gray-500 text-xs italic truncate max-w-[140px] md:max-w-[200px]">— {h.notes}</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : (
            /* Evaluator Notes */
            notes.length === 0 ? (
              <div className="text-center text-gray-400 py-8">
                <MessageSquare className="w-8 h-8 text-gray-600 mx-auto mb-2" />
                <p>No evaluator notes yet.</p>
                <p className="text-gray-500 text-xs mt-1">Notes added in the Grading tab will appear here as a timeline.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {notes.map(n => (
                  <div key={n.id} className={`bg-[#0D1229] rounded-lg border ${n.editable ? 'border-[#00D4FF]/30' : 'border-[#546E7A]/20'} p-4`}>
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-gray-500 text-xs">{n.note_date || '—'}</span>
                      <span className="text-[#00D4FF] text-xs font-medium">{n.category_name}</span>
                      {n.scout_name && <span className="text-gray-500 text-xs">by {n.scout_name}</span>}
                      {n.editable && <span className="px-1.5 py-0.5 rounded text-xs bg-[#00D4FF]/10 text-[#00D4FF]">Today — Editable</span>}
                      {!n.editable && <span className="px-1.5 py-0.5 rounded text-xs bg-gray-700 text-gray-400">Locked</span>}
                    </div>
                    <p className="text-gray-300 text-sm">{n.note_text}</p>
                  </div>
                ))}
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
}
